// The volunteer "Waitlists" tab on the Volunteer Activity page. Shows every event the user holds an
// ACTIVE waitlist entry on, grouped into one card per event; each card reuses VolunteerEventShiftsTable
// over the event's waitlisted shifts. The displayed set of cards is frozen for the session, so leaving
// a waitlist flips the row's action to "Join waitlist" in place (via live entries) instead of removing
// the card — the card only drops on a fresh page load. Active events render on load; past events (all
// waitlisted shifts ended) are revealed by the "Show past waitlists" toggle. The two sections
// paginate independently.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Alert,
  Badge,
  Box,
  Button,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import { Event, LocationType, WaitlistEntry } from '@amzn/red-velvet-api';
import { useGetAllWaitlistsByUser } from '../../hooks/shifts/shiftWaitlist';
import {
  WaitlistEventGroup,
  formatEventDateRange,
  mapActiveWaitlistEntriesByEventId,
  partitionWaitlistEventGroups,
} from '../../utils/waitlistsTabUtils';
import { VolunteerEventShiftsTable } from '../shifts/volunteerEventShiftsTable';
import { SpaLink } from '../SpaLink';
import { DEFAULT_EVENT_PAGE_SIZE } from './constants';
import { publishKatalMetric } from '../katalAnalytics';
import { VOLUNTEER_WAITLIST_PAGE_VIEW } from '../../utils/constants';

const I18N = 'waitlistsTab';

function getEventLocationBadge(
  event: Event,
  t: (key: string) => string
): { label: string; color: 'green' | 'grey' | 'blue' } {
  const location = event.location;
  if (location?.locationType === LocationType.VIRTUAL) {
    return { label: t(`${I18N}.location.virtual`), color: 'grey' };
  }
  if (location?.amazonBuilding) {
    return { label: t(`${I18N}.location.amazonBuilding`), color: 'green' };
  }
  return { label: t(`${I18N}.location.inPerson`), color: 'blue' };
}

function WaitlistEventCard({
  group,
  liveWaitlistEntries,
  onOpenInfo,
}: {
  group: WaitlistEventGroup;
  liveWaitlistEntries: WaitlistEntry[];
  onOpenInfo?: () => void;
}) {
  const { t, i18n } = useTranslation(['activity', 'translation']);
  const { event, shifts } = group;

  const badge = getEventLocationBadge(event, t);
  const organization = event.communityOrganizations?.[0]?.name;
  const cityState = [event.location?.city, event.location?.province].filter(Boolean).join(', ');
  const meta = [
    formatEventDateRange(event, i18n.language),
    cityState,
    t(`${I18N}.shiftsWaitlisted`, { count: shifts.length }),
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <VolunteerEventShiftsTable
      header={
        <SpaceBetween size="s">
          <SpaceBetween direction="horizontal" size="xs">
            <SpaLink href={`/view-event?id=${event.eventId}`}>
              <h2 style={{ margin: 0 }}>{event.title}</h2>
            </SpaLink>
            <Badge color={badge.color}>{badge.label}</Badge>
          </SpaceBetween>
          <Box color="text-body-secondary" fontSize="body-s">
            <SpaceBetween direction="horizontal" size="s">
              <span>{meta}</span>
              {onOpenInfo && (
                <Link
                  variant="info"
                  data-testid="waitlists.infoLink"
                  data-aci-analytics-name="waitlists_infoPanelOpened"
                  onFollow={(e) => {
                    e.preventDefault();
                    onOpenInfo();
                  }}
                >
                  {t(`${I18N}.info.linkLabel`)}
                </Link>
              )}
            </SpaceBetween>
          </Box>
          {organization && (
            <Box color="text-body-secondary" fontSize="body-s">
              {organization}
            </Box>
          )}
        </SpaceBetween>
      }
      event={event}
      shifts={shifts}
      signups={[]}
      attendances={[]}
      waitlistEntries={liveWaitlistEntries}
      // pageSize intentionally omitted: the card uses the table's standard default (10 shifts/page);
      // only the Event Details page enlarges it.
    />
  );
}

// A client-paginated list of event cards. Active and past sections each render their own instance, so
// their page state is fully independent and toggling the past section never disturbs the active page.
function WaitlistEventCardList({
  groups,
  liveEntriesByEventId,
  onOpenInfo,
}: {
  groups: WaitlistEventGroup[];
  liveEntriesByEventId: Map<string, WaitlistEntry[]>;
  onOpenInfo?: () => void;
}) {
  const { items, paginationProps } = useCollection(groups, {
    pagination: { pageSize: DEFAULT_EVENT_PAGE_SIZE },
  });

  return (
    <SpaceBetween size="l">
      {items.map((group) => (
        <WaitlistEventCard
          key={group.event.eventId}
          group={group}
          liveWaitlistEntries={liveEntriesByEventId.get(group.event.eventId) ?? []}
          onOpenInfo={onOpenInfo}
        />
      ))}
      {paginationProps.pagesCount > 1 && (
        <Box textAlign="center">
          <Pagination {...paginationProps} />
        </Box>
      )}
    </SpaceBetween>
  );
}

export function WaitlistsTab({ onOpenInfo }: { onOpenInfo?: () => void }) {
  const { t } = useTranslation(['activity', 'translation']);
  const [showPast, setShowPast] = useState(false);
  const { data, isLoading, error } = useGetAllWaitlistsByUser();

  // Cloudscape Tabs mounts only the active tab's content, so this fires once when the volunteer
  // opens the Waitlists tab.
  useEffect(() => {
    publishKatalMetric(VOLUNTEER_WAITLIST_PAGE_VIEW, '');
  }, []);

  const livePartition = useMemo(() => partitionWaitlistEventGroups(data ?? []), [data]);
  // Live ACTIVE entries per event, threaded into each card. Leaving a waitlist drops the entry here so
  // the row's Action flips to "Join waitlist" in place, while the frozen membership below keeps the
  // card on screen.
  const liveEntriesByEventId = useMemo(() => mapActiveWaitlistEntriesByEventId(data ?? []), [data]);
  // Freeze which events/shifts the tab shows for the session so leaving a waitlist doesn't remove the
  // card immediately; membership re-seeds only on a fresh mount (page refresh / re-navigation). Each
  // card's Leave/Join action still updates live, so the row flips to "Join waitlist" in place.
  const frozenPartitionRef = useRef<ReturnType<typeof partitionWaitlistEventGroups> | null>(null);
  if (
    frozenPartitionRef.current === null &&
    (livePartition.activeGroups.length > 0 || livePartition.pastGroups.length > 0)
  ) {
    frozenPartitionRef.current = livePartition;
  }
  const { activeGroups, pastGroups } = frozenPartitionRef.current ?? livePartition;

  if (isLoading) {
    return (
      <Box textAlign="center" padding="l">
        <StatusIndicator type="loading">{t(`${I18N}.loading`)}</StatusIndicator>
      </Box>
    );
  }

  if (error) {
    return <Alert type="error" header={t(`${I18N}.loadingError`)} />;
  }

  if (activeGroups.length === 0 && pastGroups.length === 0) {
    return (
      <Box textAlign="center" padding="l">
        <b className="pseudoHeader">{t(`${I18N}.empty`)}</b>
      </Box>
    );
  }

  return (
    <SpaceBetween size="l">
      {activeGroups.length > 0 ? (
        <WaitlistEventCardList
          groups={activeGroups}
          liveEntriesByEventId={liveEntriesByEventId}
          onOpenInfo={onOpenInfo}
        />
      ) : (
        <Box>{t(`${I18N}.noUpcoming`)}</Box>
      )}

      {pastGroups.length > 0 && (
        <SpaceBetween size="l">
          <Button
            variant="inline-link"
            iconName={showPast ? 'angle-up' : 'angle-down'}
            iconAlign="right"
            onClick={() => setShowPast((shown) => !shown)}
            data-testid="waitlists.showPastToggle"
          >
            {showPast ? t(`${I18N}.hidePast`) : t(`${I18N}.showPast`)}
          </Button>
          {showPast && (
            <WaitlistEventCardList
              groups={pastGroups}
              liveEntriesByEventId={liveEntriesByEventId}
              onOpenInfo={onOpenInfo}
            />
          )}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}
